import { DetailCard } from '../../../../DefaultCard';
import moment from 'moment';
import { HtmlBlockWithStyles } from '../../../../HtmlBlock';
import { useTranslation } from 'react-i18next';
import { orderBy } from 'lodash';
import { ColumnActionsMode } from '@fluentui/react';
import { IColumnWithType } from '@msrceng/component';
import { Revision } from '../../../../../types';

/**
 * Displays revision data in a detail list.
 */
export const RevisionListView = ({
  data,
  enableShimmer,
}: {
  data?: Revision[];
  enableShimmer: boolean;
}) => {
  const [t] = useTranslation('vulnerability');
  return (
    <DetailCard
      header={t('revisions.Revisions')}
      ariaLabelForGrid={t('revisions.Revisions')}
      id='revisions'
      items={orderBy(data, 'revisionDate', 'desc')}
      detailsListProps={{ shimmerLines: 2 }}
      enableShimmer={enableShimmer}
      columns={allRevisionColumnList}
    />
  );
};

/**
 * All columns for the revision list.
 */
const allRevisionColumnList: IColumnWithType<Revision>[] = [
  {
    key: 'version',
    name: 'version',
    columnActionsMode: ColumnActionsMode.disabled,
    minWidth: 70,
    maxWidth: 90,
    onRender: item => {
      if (!item?.version) {
        return '-';
      }

      return item.version.toLocaleString('en-US', {
        minimumFractionDigits: 1,
      });
    },
  },
  {
    columnActionsMode: ColumnActionsMode.disabled,
    key: 'revisionDate',
    name: 'revisionDate',
    onRender: item => {
      const d = moment(item?.revisionDate);
      if (!item?.revisionDate || !d?.isValid()) {
        return '-';
      }
      return d.format('ll');
    },
    minWidth: 70,
    maxWidth: 90,
    data: { isDate: true },
  },
  {
    columnActionsMode: ColumnActionsMode.disabled,
    key: 'description',
    name: 'description',
    isMultiline: true,
    minWidth: 100,
    maxWidth: 200,
    // eslint-disable-next-line react/display-name
    onRender: i => <DescriptionCell description={i?.description} />,
  },
];

/**
 * Takes a `description` and renders it as HTML.
 *
 * Renders a dash if the description is empty.
 */
const DescriptionCell = ({ description = '-' }) => (
  <HtmlBlockWithStyles>{description}</HtmlBlockWithStyles>
);
